import { onFormatShort } from './_chartScale';

const BLOCK = 'LStudio__app_chart';

/*
    🔥 MEDIDOR RADIAL

    Para un KPI contra su objetivo: cumplimiento de ventas, uso del
    espacio, avance de un proyecto.

    ⚠️ ES UN ARCO DE 270 GRADOS, NO UN CÍRCULO

    Un anillo cerrado no tiene principio ni fin visible: al 5% y al 95%
    se ven casi igual de "casi completos". Con el hueco abajo, el
    recorrido se lee de un vistazo.

    ⚠️ EL ARCO SE HACE CON stroke-dasharray, COMO LA DONA

    Mismo motivo que allá: calcular un path de arco a mano se rompe al
    pasar la media vuelta por las banderas large-arc y sweep.

    ⚠️ PUEDE PASARSE DEL 100%

    Vender un 120% del objetivo es una noticia buena, no un error. El
    arco se corta en el tope pero el número muestra el valor real.
*/

const RADIUS = 70;
const SWEEP = 0.75; // 270° de 360°
const ARC = 2 * Math.PI * RADIUS * SWEEP;

export const ChartGauge = ({
    value = 0, max = 100, label = null, unit = '%',
    tone = 'accent', size = 180, thickness = 14, showTicks = true,
    formatValue = null, thresholds = null, // [{ at: 50, tone: 'warn' }, { at: 80, tone: 'ok' }]
}) => {

    const RAW = Number(value) || 0;

    const PERCENT = max ? (RAW / max) * 100 : 0;

    const CLAMPED = Math.min(100, Math.max(0, PERCENT));

    /*
        🔥 El tono puede salir del valor: por debajo del primer umbral
           va el tono base, y de ahí para arriba el que corresponda.
    */
    const TONE = thresholds ? ([...thresholds].sort((a, b) => b.at - a.at).find((t) => PERCENT >= t.at)?.tone ?? tone) : tone;

    const FILLED = (CLAMPED / 100) * ARC;

    const TEXT = formatValue ? formatValue(RAW, max) : `${Math.round(PERCENT)}${unit}`;

    return (
        <div className={`${BLOCK}_gauge`} data-tone={TONE} style={{ width: size, height: size * 0.82 }}>

            {/* 🔥 -225° pone el hueco abajo y centrado */}
            <svg viewBox="0 0 180 180" style={{ transform: 'rotate(135deg)' }}
                role="img" aria-label={`${label ?? 'Medidor'}: ${TEXT}`} >

                <circle className={`${BLOCK}_gauge__track`} cx="90" cy="90" r={RADIUS} strokeWidth={thickness} strokeDasharray={`${ARC} ${2 * Math.PI * RADIUS}`}
                    strokeLinecap="round" />

                <circle className={`${BLOCK}_gauge__fill`} cx="90" cy="90" r={RADIUS} strokeWidth={thickness} strokeDasharray={`${FILLED} ${2 * Math.PI * RADIUS}`} strokeLinecap="round" />

                {
                    showTicks && [0, 25, 50, 75, 100].map((tick) => {

                        // 🔥 El arco arranca en 0 y barre 270°
                        const ANGLE = (tick / 100) * SWEEP * 2 * Math.PI;

                        const OUT = RADIUS + thickness / 2 + 4;
                        const IN = RADIUS + thickness / 2 + 1;

                        return (
                            <line  key={tick}   className={`${BLOCK}_gauge__tick`}
                                x1={90 + IN * Math.cos(ANGLE)}   y1={90 + IN * Math.sin(ANGLE)}
                                x2={90 + OUT * Math.cos(ANGLE)}   y2={90 + OUT * Math.sin(ANGLE)}
                            />
                        );
                    })
                }

            </svg>

            <div className={`${BLOCK}_gauge__center`}>

                <strong>{TEXT}</strong>

                {label && <span>{label}</span>}

                {
                    PERCENT > 100 && (
                        <em className={`${BLOCK}_gauge__over`}>
                            +{Math.round(PERCENT - 100)}% sobre el objetivo
                        </em>
                    )
                }

            </div>

            {
                max && (
                    <span className={`${BLOCK}_gauge__max`}>
                        Objetivo: {onFormatShort(max)}
                    </span>
                )
            }

        </div>
    );
};
